import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
	Select,
	Button,
	Card,
	DatePicker,
	Input,
	message,
	Modal,
	Popover,
	Typography,
	Alert,
	Spin,
	Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import {
	ExportOutlined,
	InfoCircleOutlined,
	StopOutlined,
} from '@ant-design/icons';
import {
	CreatePointsRevokeEvent,
	QueryPointsEventsByCompanyId,
	QueryPointsEventsPostgres,
	formatDate,
	formatDateUTC,
	formatActionTypeForExport,
	getEventAttribution,
	formatUserName,
	isRevocableEvent,
} from './pointsSettingsHelpers';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

const EARLIEST_YEAR = 2026;
const EARLIEST_QUARTER = { year: 2026, quarter: 1 };
const EARLIEST_MONTH = { year: 2026, month: 2 }; // February 2026 (1-indexed)

const buildYearOptions = () => {
	const currentYear = new Date().getFullYear();
	const options = [];
	for (let y = currentYear; y >= EARLIEST_YEAR; y--) {
		options.push({
			value: `${y}`,
			label: y === currentYear ? 'Current Year' : `${y}`,
		});
	}

	return options;
};

const buildQuarterOptions = () => {
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
	const options = [];
	let y = currentYear;
	let q = currentQuarter;
	while (
		y > EARLIEST_QUARTER.year ||
		(y === EARLIEST_QUARTER.year && q >= EARLIEST_QUARTER.quarter)
	) {
		const isCurrentQuarter = y === currentYear && q === currentQuarter;
		options.push({
			value: `${y}-Q${q}`,
			label: isCurrentQuarter ? 'Current Quarter' : `Q${q} ${y}`,
		});
		q--;
		if (q < 1) {
			q = 4;
			y--;
		}
	}

	return options;
};

const buildMonthOptions = () => {
	const now = new Date();
	const currentYear = now.getFullYear();
	const currentMonth = now.getMonth() + 1; // 1-indexed
	const options = [];
	let y = currentYear;
	let m = currentMonth;
	while (
		y > EARLIEST_MONTH.year ||
		(y === EARLIEST_MONTH.year && m >= EARLIEST_MONTH.month)
	) {
		const isCurrentMonth = y === currentYear && m === currentMonth;
		options.push({
			value: `${y}-${String(m).padStart(2, '0')}`,
			label: isCurrentMonth ? 'Current Month' : `${MONTH_NAMES[m - 1]} ${y}`,
		});
		m--;
		if (m < 1) {
			m = 12;
			y--;
		}
	}

	return options;
};

// Format action type to human readable text (returns JSX)
const formatActionType = (actionType, event) => {
	const contact = event.referral?.contact;
	const contactName = contact ? `${contact.firstName} ${contact.lastName}` : '';
	const contactEmail = contact?.emailAddress
		? ` (${contact.emailAddress})`
		: contact?.phoneNumber
			? ` (${contact.phoneNumber})`
			: '';

	const isRevokedDebit =
		event.direction === 'DEBIT' &&
		event.sourceType === 'Revoke' &&
		actionType !== 'POINTS_SPENT';
	const revokerName = formatUserName(event.actorUser);
	const revokerEmail = event.actorUser?.emailAddress;
	const revokedSuffix = isRevokedDebit
		? revokerName
			? ` that was revoked by ${revokerName}${
					revokerEmail ? ` (${revokerEmail})` : ''
				}`
			: ' that was revoked'
		: '';

	switch (actionType) {
		case 'REFERRAL_SENT':
			return (
				<>
					sending a <Link to={`/referrals/${event.sourceId}`}>referral</Link> to{' '}
					{contactName}
					{contactEmail}
					{revokedSuffix}
				</>
			);
		case 'REFERRAL_GENERAL_SENT':
			return (
				<>
					a <Link to={`/referrals/${event.sourceId}`}>referral</Link> that was
					accepted by {contactName}
					{contactEmail}
					{revokedSuffix}
				</>
			);
		case 'REFERRAL_TO_ACCEPTED':
			return (
				<>
					a <Link to={`/referrals/${event.sourceId}`}>referral</Link> to{' '}
					{contactName}
					{contactEmail} being accepted{revokedSuffix}
				</>
			);
		case 'REFERRAL_TO_INTERVIEWING':
			return (
				<>
					a <Link to={`/referrals/${event.sourceId}`}>referral</Link> to{' '}
					{contactName}
					{contactEmail} moving to interviewing{revokedSuffix}
				</>
			);
		case 'REFERRAL_TO_HIRED':
			return (
				<>
					a <Link to={`/referrals/${event.sourceId}`}>referral</Link> to{' '}
					{contactName}
					{contactEmail} being hired{revokedSuffix}
				</>
			);
		case 'REFERRAL_LINK_SHARED':
			return <>sharing a referral link{revokedSuffix}</>;
		case 'REFERRAL_FROM_SHARE':
			return (
				<>
					a <Link to={`/referrals/${event.sourceId}`}>referral</Link> from a
					social media share{revokedSuffix}
				</>
			);
		case 'FIRST_MOBILE_LOGIN':
			return <>a first mobile login{revokedSuffix}</>;
		case 'WEEKLY_LOGIN_STREAK':
			return <>a weekly login streak{revokedSuffix}</>;
		case 'RECOGNITION_SENT':
			return <>sending a recognition{revokedSuffix}</>;
		case 'RECOGNITION_RECEIVED':
			return <>receiving a recognition{revokedSuffix}</>;
		case 'POINTS_FROM_BADGE':
			return <>the points value set on a received recognition{revokedSuffix}</>;
		case 'POINTS_SPENT':
			return event.direction === 'CREDIT'
				? 'a points refund'
				: 'spending points';
		default:
			return (
				<>
					{actionType.toLowerCase().replace(/_/g, ' ')}
					{revokedSuffix}
				</>
			);
	}
};

// A small info icon that reveals event details (revoke reason, who revoked)
// in a hover popover.
const DetailsPopover = ({ content }) => (
	<Popover content={content} trigger="hover" placement="top">
		<InfoCircleOutlined
			style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'pointer' }}
		/>
	</Popover>
);

// Renders the trailing "(Revoked)" label / details icon / "Added by …"
// enrichment for a log row. For reversal DEBIT entries the revoker's name &
// email are rendered inline in the sentence (see formatActionType); the icon's
// popover carries the reason.
const renderEnrichment = (event) => {
	const attribution = getEventAttribution(event);
	if (!attribution) return null;
	const name = formatUserName(attribution.user);
	const email = attribution.user?.emailAddress;
	const reason = attribution.reason;

	if (attribution.type === 'REVOKE_DEBIT') {
		if (!reason) return null;
		return (
			<DetailsPopover
				content={
					<div style={{ maxWidth: 280 }}>
						<strong>Reason:</strong> {reason}
					</div>
				}
			/>
		);
	}

	if (attribution.type === 'REVOKED_ORIGINAL') {
		return (
			<>
				<span style={{ color: '#e74c3c', fontWeight: 600 }}> (Revoked)</span>
				{(name || reason) && (
					<DetailsPopover
						content={
							<div style={{ maxWidth: 280 }}>
								{name && (
									<div>
										<strong>Revoked by:</strong> {name}
										{email ? ` (${email})` : ''}
									</div>
								)}
								{reason && (
									<div style={{ marginTop: name ? 4 : 0 }}>
										<strong>Reason:</strong> {reason}
									</div>
								)}
							</div>
						}
					/>
				)}
			</>
		);
	}

	// TRIGGERED (custom points added via createPointsTriggerEvent)
	if (!name) return null;
	const addedByLabel = (
		<span
			style={{
				color: '#595959',
				...(reason ? { cursor: 'help', borderBottom: '1px dotted #999' } : {}),
			}}
		>
			{` Added by ${name}.`}
		</span>
	);
	if (!reason) return addedByLabel;
	return (
		<Popover
			content={
				<div style={{ maxWidth: 280 }}>
					<strong>Reason:</strong> {reason}
				</div>
			}
			trigger="hover"
			placement="top"
		>
			{addedByLabel}
		</Popover>
	);
};

const PointsLog = () => {
	const [pointsEvents, setPointsEvents] = useState([]);
	const [pointsNextToken, setPointsNextToken] = useState(null);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [exportFormat, setExportFormat] = useState(null);
	const [exportPeriodType, setExportPeriodType] = useState(null);
	const [exportPeriodValue, setExportPeriodValue] = useState(null);
	const [exportDateRange, setExportDateRange] = useState(null);
	const [exporting, setExporting] = useState(false);
	const [revokeTarget, setRevokeTarget] = useState(null);
	const [revokeReason, setRevokeReason] = useState('');
	const [revoking, setRevoking] = useState(false);
	const [hoveredIndex, setHoveredIndex] = useState(null);
	const logContainerRef = useRef(null);

	const yearOptions = useMemo(() => buildYearOptions(), []);
	const quarterOptions = useMemo(() => buildQuarterOptions(), []);
	const monthOptions = useMemo(() => buildMonthOptions(), []);

	const client = useApolloClient();
	const currentUser = useSelector((state) => state?.user?.currentUser);

	const fetchPointsEvents = useCallback(
		async (reset = false) => {
			if (!currentUser?.companyId) return;
			if (loadingEvents) return;

			try {
				setLoadingEvents(true);
				const { data } = await client.query({
					query: QueryPointsEventsByCompanyId,
					variables: {
						companyId: currentUser.companyId,
						first: 50,
						after: reset ? null : pointsNextToken,
					},
					fetchPolicy: 'network-only',
				});

				const result = data?.queryPointsEventsByCompanyId;
				if (result) {
					setPointsEvents((prev) =>
						reset ? result.items : [...prev, ...result.items]
					);
					setPointsNextToken(result.nextToken);
				}
			} catch (error) {
				console.error('Error fetching points events:', error);
				message.error('Failed to load points log');
			} finally {
				setLoadingEvents(false);
			}
		},
		[client, currentUser?.companyId, pointsNextToken, loadingEvents]
	);

	useEffect(() => {
		if (currentUser?.companyId) {
			fetchPointsEvents(true);
		}
	}, [currentUser?.companyId]);

	const handleScroll = useCallback(
		(e) => {
			const { scrollTop, scrollHeight, clientHeight } = e.target;
			if (
				scrollHeight - scrollTop <= clientHeight * 1.5 &&
				pointsNextToken &&
				!loadingEvents
			) {
				fetchPointsEvents(false);
			}
		},
		[pointsNextToken, loadingEvents, fetchPointsEvents]
	);

	const closeRevokeModal = () => {
		if (revoking) return;
		setRevokeTarget(null);
		setRevokeReason('');
	};

	const handleRevoke = async () => {
		if (!revokeTarget?.id) {
			message.error('Cannot revoke: this event is missing an id.');
			return;
		}

		const reason = revokeReason.trim();
		if (!reason) return;

		try {
			setRevoking(true);
			await client.mutate({
				mutation: CreatePointsRevokeEvent,
				variables: {
					input: {
						companyId: currentUser.companyId,
						originalPointsEventId: revokeTarget.id,
						revokedByUserId: currentUser.id,
						reason,
					},
				},
			});
			message.success('Points event revoked.');
			setRevokeTarget(null);
			setRevokeReason('');
			// The reversal entry is produced asynchronously by the points engine,
			// so give it a moment before refreshing the log.
			setTimeout(() => fetchPointsEvents(true), 1500);
		} catch (error) {
			console.error('Error revoking points event:', error);
			message.error('Failed to revoke points event.');
		} finally {
			setRevoking(false);
		}
	};

	const isExportReady = () => {
		if (!exportFormat || !exportPeriodType) return false;
		if (exportPeriodType === 'ALL_TIME') return true;
		if (exportPeriodType === 'CUSTOM_RANGE')
			return exportDateRange && exportDateRange[0] && exportDateRange[1];
		return !!exportPeriodValue;
	};

	const handleExportPointsLog = async () => {
		setExporting(true);
		try {
			const variables = {
				companyId: currentUser.companyId,
				first: 1999,
				after: null,
				allTime: exportPeriodType === 'ALL_TIME',
				year: null,
				quarter: null,
				month: null,
				range: null,
			};

			if (exportPeriodType === 'BY_YEAR') {
				variables.year = exportPeriodValue;
			} else if (exportPeriodType === 'BY_QUARTER') {
				variables.quarter = exportPeriodValue;
			} else if (exportPeriodType === 'BY_MONTH') {
				variables.month = exportPeriodValue;
			} else if (exportPeriodType === 'CUSTOM_RANGE') {
				variables.range = [
					exportDateRange[0].format('YYYY-MM-DD'),
					exportDateRange[1].format('YYYY-MM-DD'),
				];
			}

			let allEvents = [];
			let nextToken = null;

			do {
				variables.after = nextToken;
				const { data } = await client.query({
					query: QueryPointsEventsPostgres,
					variables,
					fetchPolicy: 'network-only',
				});

				const result = data?.queryPointsEventsPostgres;
				if (result?.items) {
					allEvents = [...allEvents, ...result.items];
					nextToken = result.nextToken;
				} else {
					break;
				}
			} while (nextToken);

			if (allEvents.length === 0) {
				message.warning('No events found for the selected period');
				setExporting(false);
				return;
			}

			let content = '';
			let filename = '';

			if (exportFormat === 'CSV') {
				// CSV format
				const capitalize = (str) =>
					str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
				const rows = [
					[
						'Date',
						'Recipient First Name',
						'Recipient Last Name',
						'Recipient Email',
						'Candidate First Name',
						'Candidate Last Name',
						'Candidate Email',
						'Candidate Phone',
						'Type',
						'Amount',
						'Event',
						'Revoked',
						'Actioned By',
						'Reason / Note',
					],
				];

				allEvents.forEach((event) => {
					const user = event.user;
					const userFirstName = capitalize(user?.firstName) || 'N/A';
					const userLastName = capitalize(user?.lastName) || 'N/A';
					const userEmail = user?.emailAddress || '';
					const contact = event.referral?.contact;
					const contactFirstName = capitalize(contact?.firstName) || 'N/A';
					const contactLastName = capitalize(contact?.lastName) || 'N/A';
					const contactEmail = contact?.emailAddress || 'N/A';
					const contactPhone = contact?.phoneNumber || 'N/A';
					const dateStr = formatDateUTC(event.createdAt);
					const eventDesc = capitalize(
						formatActionTypeForExport(event.actionType, event)
					);
					const attribution = getEventAttribution(event);
					const attributionName = formatUserName(attribution?.user);
					const attributionEmail = attribution?.user?.emailAddress || '';
					const actionedBy = attributionName
						? attributionEmail
							? `${attributionName} (${attributionEmail})`
							: attributionName
						: '';
					const reasonNote = attribution?.reason || '';

					rows.push([
						dateStr,
						userFirstName,
						userLastName,
						userEmail,
						contactFirstName,
						contactLastName,
						contactEmail,
						contactPhone,
						event.direction === 'DEBIT' ? 'Deducted' : 'Earned',
						event.amount.toString(),
						eventDesc,
						event.revoked ? 'Yes' : 'No',
						actionedBy,
						reasonNote,
					]);
				});

				content = rows
					.map((row) =>
						row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')
					)
					.join('\n');

				const periodSlug =
					exportPeriodType === 'CUSTOM_RANGE'
						? `${exportDateRange[0].format('YYYY-MM-DD')}_to_${exportDateRange[1].format('YYYY-MM-DD')}`
						: (exportPeriodValue || exportPeriodType).toLowerCase();
				filename = `points-log-${periodSlug}-${new Date().toISOString().split('T')[0]}.csv`;
			} else {
				// TXT format
				const lines = allEvents.map((event) => {
					const capitalize = (str) =>
						str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
					const user = event.user;
					const userFirstName = capitalize(user?.firstName) || 'N/A';
					const userLastName = capitalize(user?.lastName) || 'N/A';
					const userEmail = user?.emailAddress || '';
					const contact = event.referral?.contact;
					const contactFirstName = capitalize(contact?.firstName) || 'N/A';
					const contactLastName = capitalize(contact?.lastName) || 'N/A';
					const contactEmail = contact?.emailAddress || 'N/A';
					const contactPhone = contact?.phoneNumber || 'N/A';
					const dateStr = formatDateUTC(event.createdAt);
					const eventDesc = capitalize(
						formatActionTypeForExport(event.actionType, event)
					);
					const verb = event.direction === 'DEBIT' ? 'was deducted' : 'earned';
					const revokedTag = event.revoked ? ' (Revoked)' : '';
					const attribution = getEventAttribution(event);
					const attributionName = formatUserName(attribution?.user);
					const attributionEmail = attribution?.user?.emailAddress || '';
					const actionedBy = attributionName
						? attributionEmail
							? `${attributionName} (${attributionEmail})`
							: attributionName
						: '';
					const reasonNote = attribution?.reason || '';
					const attributionTag = actionedBy
						? ` [Actioned by ${actionedBy}${reasonNote ? `: ${reasonNote}` : ''}]`
						: '';

					return `${dateStr} - ${userFirstName} ${userLastName} (${userEmail}) ${verb} ${event.amount} points for ${eventDesc}. Candidate: ${contactFirstName} ${contactLastName}, ${contactEmail}, ${contactPhone}${revokedTag}${attributionTag}`;
				});

				content = lines.join('\n');
				const periodSlugTxt =
					exportPeriodType === 'CUSTOM_RANGE'
						? `${exportDateRange[0].format('YYYY-MM-DD')}_to_${exportDateRange[1].format('YYYY-MM-DD')}`
						: (exportPeriodValue || exportPeriodType).toLowerCase();
				filename = `points-log-${periodSlugTxt}-${new Date().toISOString().split('T')[0]}.txt`;
			}

			const blob = new Blob([content], {
				type: exportFormat === 'CSV' ? 'text/csv' : 'text/plain',
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);

			message.success('Points log exported successfully');
		} catch (error) {
			console.error('Error exporting points log:', error);
			message.error('Failed to export points log');
		} finally {
			setExporting(false);
		}
	};

	return (
		<Card
			title={<span style={{ fontWeight: 600 }}>Points Log</span>}
			style={{
				marginBottom: '24px',
				borderRadius: '12px',
				border: '1px solid #ddd',
			}}
			extra={
				<div
					style={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<Select
						value={exportFormat}
						onChange={setExportFormat}
						style={{ width: 120 }}
						disabled={exporting}
						placeholder="File Type"
					>
						<Option value="CSV">CSV</Option>
						<Option value="TXT">TXT</Option>
					</Select>
					<Select
						value={exportPeriodType}
						onChange={(value) => {
							setExportPeriodType(value);
							setExportDateRange(null);
							if (value === 'ALL_TIME' || value === 'CUSTOM_RANGE') {
								setExportPeriodValue(null);
							} else if (value === 'BY_YEAR') {
								setExportPeriodValue(yearOptions[0]?.value || null);
							} else if (value === 'BY_QUARTER') {
								setExportPeriodValue(quarterOptions[0]?.value || null);
							} else if (value === 'BY_MONTH') {
								setExportPeriodValue(monthOptions[0]?.value || null);
							}
						}}
						style={{ width: 160 }}
						disabled={exporting}
						placeholder="Period Type"
					>
						<Option value="ALL_TIME">All Time</Option>
						<Option value="BY_YEAR">By Year</Option>
						<Option value="BY_QUARTER">By Quarter</Option>
						<Option value="BY_MONTH">By Month</Option>
						<Option value="CUSTOM_RANGE">Custom Range</Option>
					</Select>
					<div style={{ display: 'flex', overflow: 'hidden' }}>
						<div
							style={{
								maxWidth: exportPeriodType === 'BY_YEAR' ? 150 : 0,
								opacity: exportPeriodType === 'BY_YEAR' ? 1 : 0,
								overflow: 'hidden',
								transition: 'max-width 0.3s ease, opacity 0.3s ease',
							}}
						>
							<Select
								value={
									exportPeriodType === 'BY_YEAR' ? exportPeriodValue : undefined
								}
								onChange={setExportPeriodValue}
								style={{ width: 150 }}
								disabled={exporting}
								options={yearOptions}
							/>
						</div>
						<div
							style={{
								maxWidth: exportPeriodType === 'BY_QUARTER' ? 170 : 0,
								opacity: exportPeriodType === 'BY_QUARTER' ? 1 : 0,
								overflow: 'hidden',
								transition: 'max-width 0.3s ease, opacity 0.3s ease',
							}}
						>
							<Select
								value={
									exportPeriodType === 'BY_QUARTER'
										? exportPeriodValue
										: undefined
								}
								onChange={setExportPeriodValue}
								style={{ width: 170 }}
								disabled={exporting}
								options={quarterOptions}
							/>
						</div>
						<div
							style={{
								maxWidth: exportPeriodType === 'BY_MONTH' ? 170 : 0,
								opacity: exportPeriodType === 'BY_MONTH' ? 1 : 0,
								overflow: 'hidden',
								transition: 'max-width 0.3s ease, opacity 0.3s ease',
							}}
						>
							<Select
								value={
									exportPeriodType === 'BY_MONTH'
										? exportPeriodValue
										: undefined
								}
								onChange={setExportPeriodValue}
								style={{ width: 170 }}
								disabled={exporting}
								options={monthOptions}
							/>
						</div>
						<div
							style={{
								maxWidth: exportPeriodType === 'CUSTOM_RANGE' ? 280 : 0,
								opacity: exportPeriodType === 'CUSTOM_RANGE' ? 1 : 0,
								overflow: 'hidden',
								transition: 'max-width 0.3s ease, opacity 0.3s ease',
							}}
						>
							<RangePicker
								value={
									exportPeriodType === 'CUSTOM_RANGE' ? exportDateRange : null
								}
								onChange={setExportDateRange}
								disabled={exporting}
								format="YYYY-MM-DD"
							/>
						</div>
					</div>
					<Tooltip
						title={
							!isExportReady() ? 'Please select export options first.' : ''
						}
					>
						<Button
							icon={<ExportOutlined />}
							onClick={handleExportPointsLog}
							loading={exporting}
							disabled={!isExportReady() || exporting}
							style={
								!isExportReady() || exporting
									? { border: '1px solid #ddd', color: '#ddd' }
									: {}
							}
						>
							Export Log
						</Button>
					</Tooltip>
				</div>
			}
		>
			<div
				ref={logContainerRef}
				onScroll={handleScroll}
				style={{
					maxHeight: '600px',
					overflowY: 'auto',
					padding: '0 8px',
				}}
			>
				{loadingEvents && pointsEvents.length === 0 ? (
					<div style={{ textAlign: 'center', padding: '40px' }}>
						<Spin size="large" tip="Loading points log..." />
					</div>
				) : pointsEvents.length === 0 ? (
					<Alert
						message="No points events yet"
						description="Points events will appear here as employees earn points"
						type="info"
						showIcon
					/>
				) : (
					<>
						{pointsEvents.map((event, index) => {
							const user = event.user;
							const userName = user
								? `${user.firstName} ${user.lastName}`
								: 'Unknown User';
							const userEmail = user?.emailAddress || '';
							const isDebit = event.direction === 'DEBIT';
							const isRevoked = event.revoked === true;
							const verb = isDebit ? 'was deducted' : 'earned';
							const pointsColor = isDebit ? '#e74c3c' : '#27ae60';

							return (
								<div
									key={`${event.createdAt}-${index}`}
									onMouseEnter={() => setHoveredIndex(index)}
									onMouseLeave={() =>
										setHoveredIndex((cur) => (cur === index ? null : cur))
									}
									style={{
										display: 'flex',
										alignItems: 'flex-start',
										gap: 8,
										padding: '12px 0',
										borderBottom:
											index < pointsEvents.length - 1
												? '1px solid #f0f0f0'
												: 'none',
										lineHeight: '1.6',
										opacity: isRevoked ? 0.45 : 1,
									}}
								>
									<Text style={{ flex: 1 }}>
										<Tooltip
											title={formatDateUTC(event.createdAt)}
											placement="top"
											getPopupContainer={(trigger) =>
												trigger.parentElement || document.body
											}
										>
											<span
												style={{
													cursor: 'help',
													borderBottom: '1px dotted #999',
												}}
											>
												{formatDate(event.createdAt)}
											</span>
										</Tooltip>{' '}
										- <Link to={`/employees/${event.userId}`}>{userName}</Link>{' '}
										({userEmail}) {verb}{' '}
										<strong style={{ color: pointsColor }}>
											{event.amount} points
										</strong>{' '}
										for {formatActionType(event.actionType, event)}.
										{renderEnrichment(event)}
									</Text>
									{isRevocableEvent(event) && (
										<span
											style={{
												opacity: hoveredIndex === index ? 1 : 0,
												transform:
													hoveredIndex === index ? 'scale(1)' : 'scale(0.8)',
												transition:
													'opacity 0.2s ease, transform 0.2s ease',
												pointerEvents:
													hoveredIndex === index ? 'auto' : 'none',
											}}
										>
											<Tooltip title="Revoke this event">
												<Button
													type="text"
													size="small"
													danger
													icon={<StopOutlined />}
													onClick={() => {
														setRevokeTarget(event);
														setRevokeReason('');
													}}
												/>
											</Tooltip>
										</span>
									)}
								</div>
							);
						})}
						{loadingEvents && (
							<div style={{ textAlign: 'center', padding: '20px' }}>
								<Spin />
							</div>
						)}
						{!loadingEvents && !pointsNextToken && pointsEvents.length > 0 && (
							<div
								style={{
									textAlign: 'center',
									padding: '20px',
									color: '#595959',
								}}
							>
								<Text type="secondary">
									All log entries have been retrieved
								</Text>
							</div>
						)}
					</>
				)}
			</div>
			<Modal
				title="Revoke points event"
				open={!!revokeTarget}
				onCancel={closeRevokeModal}
				onOk={handleRevoke}
				okText="Revoke"
				okButtonProps={{
					danger: true,
					loading: revoking,
					disabled: !revokeReason.trim(),
				}}
				cancelButtonProps={{ disabled: revoking }}
				maskClosable={!revoking}
			>
				<p>
					This will revoke the selected points event and reverse the points it
					granted. A reason is required.
				</p>
				<Input.TextArea
					rows={3}
					value={revokeReason}
					onChange={(e) => setRevokeReason(e.target.value)}
					placeholder="Reason for revoking this event"
					maxLength={500}
					disabled={revoking}
				/>
			</Modal>
		</Card>
	);
};

export default PointsLog;
